import CropModal from "@components/core/modals/CropModal";
import { Image } from "@type/core/image/image.types";
import { Picture } from "@type/core/offices/offices.types";
import { PNG_CONTENT_TYPE } from "@utils/Constant";
import { addBase64UrlPrefix, base64ToBlob } from "@utils/helpers/base64-helper";
import { FunctionComponent, useState } from "react";
import UploadImageField from "@components/core/inputs/UploadImageField";
import BasicButton from "@components/core/buttons/BasicButton";
import { useTranslation } from "react-i18next";
import { SignaturePicturePriorityEnum } from "@utils/enums/signature.picture.priority.enum";

export interface SignaturePicturesProps {
  signaturePictures?: Picture[];
  onUpdatePicture: (rank: number, file?: File, callback?: () => void) => void;
  onDeletePicture: (
    signaturePriority: SignaturePicturePriorityEnum,
    rank: number,
    callback?: () => void,
  ) => void;
  onSetDefaultPicture?: (
    signaturePriority: SignaturePicturePriorityEnum,
    rank?: number,
    callback?: () => void,
  ) => void;
  signatureType?: SignaturePicturePriorityEnum;
  signatureSelected?: SignaturePicturePriorityEnum;
  showButton?: boolean;
  row?: boolean;
}

const SignaturePicture: FunctionComponent<SignaturePicturesProps> = ({
  signaturePictures,
  onUpdatePicture,
  onDeletePicture,
  onSetDefaultPicture,
  signatureType = SignaturePicturePriorityEnum.PERSONAL_PICTURE,
  signatureSelected,
  showButton = true,
  row = false,
}) => {
  const { t } = useTranslation();

  const [cropModalVisible, setCropModalVisible] = useState<boolean>(false);
  const [newSignaturePicture, setNewSignaturePicture] = useState<
    Picture | undefined
  >(undefined);
  const [signatureImageType, setSignatureImageType] = useState<string>();

  const updateSignature = (picture?: Picture, imageType?: string) => {
    setNewSignaturePicture(picture);
    setSignatureImageType(imageType);
  };

  const handleUpdatePicture = (rank: number, file?: File) => {
    onUpdatePicture(rank, file);
    updateSignature();
  };

  const handleSetImage = (rank: number, image?: Image) => {
    if (image) {
      updateSignature(
        {
          picture: addBase64UrlPrefix(
            image?.base64Data ?? "",
            PNG_CONTENT_TYPE,
          ),
          rank,
        },
        image.type,
      );
      setCropModalVisible(true);
    } else {
      onDeletePicture(signatureType, rank);
    }
  };

  const isSelected = (signaturePicture: Picture) => {
    if (signatureSelected) {
      if (signatureSelected === SignaturePicturePriorityEnum.PERSONAL_PICTURE) {
        return signaturePicture.isDefault;
      }
      return (
        signatureSelected === SignaturePicturePriorityEnum.OFFICE_PICTURE ||
        signatureSelected === SignaturePicturePriorityEnum.SCELLEMENT_PICTURE
      );
    } else {
      return !!(
        signaturePicture?.picture &&
        signaturePicture.picture.length > 0 &&
        signaturePicture.isDefault
      );
    }
  };

  return (
    <div className="row-gap-m">
      {signaturePictures?.map((signaturePicture, index) => (
        <div
          key={`SignaturePicture_SignatureCell_${index}`}
          className={`d-flex align-items-center gap-16 ${row ? "flex-row" : "flex-column"}`}
        >
          <UploadImageField
            showInfos={false}
            showMetadata={false}
            field="picture"
            imageContent={
              signaturePicture.picture
                ? {
                    file: base64ToBlob(
                      signaturePicture.picture,
                      PNG_CONTENT_TYPE,
                    ),
                    base64Data: signaturePicture.picture,
                    type: PNG_CONTENT_TYPE,
                    name: `Image de signature de rang ${signaturePicture.rank}`,
                  }
                : undefined
            }
            setImage={(image?: Image) =>
              handleSetImage(signaturePicture.rank, image)
            }
            isSelected={isSelected(signaturePicture)}
            isDefaultImage={signaturePicture.isDefaultImage}
            isSignatureImage
            sizeErrorI18nKey="account.mySignature.signaturePicture.cropModal.imageTooBig"
          />
          {
            // if there is no picture it means that there was no signature picture for this rank
            showButton &&
              signaturePicture.picture != "" &&
              (isSelected(signaturePicture) ? (
                <span>
                  {t("account.mySignature.signaturePicture.selected")}
                </span>
              ) : (
                <BasicButton
                  text={t("account.mySignature.signaturePicture.select")}
                  onClick={() =>
                    onSetDefaultPicture?.(signatureType, signaturePicture.rank)
                  }
                />
              ))
          }
          <CropModal
            key={newSignaturePicture?.picture ?? ""} // Forcer l'usage d'un nouveau cropper à chaque image
            sourcePicture={newSignaturePicture?.picture ?? ""}
            sourceImageType={signatureImageType ?? PNG_CONTENT_TYPE}
            visible={cropModalVisible}
            onCancel={() => {
              updateSignature();
              setCropModalVisible(false);
            }}
            onUpdatePicture={(file) => {
              if (newSignaturePicture) {
                handleUpdatePicture(newSignaturePicture?.rank, file);
              }
            }}
          />
        </div>
      ))}
    </div>
  );
};

export default SignaturePicture;
